import type { Vec3 } from '@rcs/domain';

/** 全体俯瞰カメラのプリセット（FR-CAM-005）。 */
export type CameraPreset = 'top' | 'front' | 'side' | 'oblique';

/** プリセットの一覧（表示順）。 */
export const CAMERA_PRESETS: readonly CameraPreset[] = ['top', 'front', 'side', 'oblique'];

/**
 * 注視点からカメラへ向かう単位ベクトル。
 *
 * - `top`：真上（わずかに手前へ傾け、向きを一意にする）
 * - `front`：+Z 側から、やや見下ろす（水平から約 10°）
 * - `side`：+X 側から、やや見下ろす
 * - `oblique`：斜め上から
 */
export function presetDirection(preset: CameraPreset): Vec3 {
  const raw: Vec3 =
    preset === 'top'
      ? [0, 1, 0.001]
      : preset === 'front'
        ? [0, 0.18, 1]
        : preset === 'side'
          ? [1, 0.18, 0]
          : [0.9, 0.8, 1.4];
  const length = Math.hypot(raw[0], raw[1], raw[2]);
  return [raw[0] / length, raw[1] / length, raw[2] / length];
}

/**
 * 半径 `radius` の球がちょうど画面に収まるカメラ距離。
 * 縦・横のうち狭いほうの視野角に合わせる。
 *
 * @param verticalFovDegrees 縦方向の視野角 [度]
 * @param aspect 画面の幅 / 高さ
 * @param margin 余白の倍率（既定 1.15）
 */
export function fitDistance(
  radius: number,
  verticalFovDegrees: number,
  aspect: number,
  margin = 1.15,
): number {
  const vertical = (verticalFovDegrees * Math.PI) / 180;
  const horizontal = 2 * Math.atan(Math.tan(vertical / 2) * aspect);
  const narrow = Math.min(vertical, horizontal);
  return (radius * margin) / Math.sin(narrow / 2);
}

/** プリセットのカメラ位置（注視点 `center` から、全体が収まる距離）。 */
export function presetPosition(
  preset: CameraPreset,
  center: Vec3,
  radius: number,
  verticalFovDegrees: number,
  aspect: number,
): Vec3 {
  const direction = presetDirection(preset);
  const distance = fitDistance(radius, verticalFovDegrees, aspect);
  return [
    center[0] + direction[0] * distance,
    center[1] + direction[1] * distance,
    center[2] + direction[2] * distance,
  ];
}
